import { useState, useEffect, useMemo } from "react";
import { groupHistoricThreadsByWorkspace } from "@/lib/groupHistoricThreadsByWorkspace";
import { patchVisitorEmail, isHumanAgentInboxLive, patchSessionTeamInbox } from "@/api/inboxAgentApi";
import { surfaceInboxHttpError } from "@/lib/inboxErrorToast";
import { ZendeskTicketCreateDebug } from "@/lib/zendeskTicketCreateDebug";
import { copyTextToClipboard } from "@/utils/copyTextToClipboard";
import { useCustomerHistory } from "../hooks/useCustomerHistory";
import CreateTicketModal from "@/pages/tickets/components/CreateTicketModal";
import HistoricThreadModal from "./HistoricThreadModal";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function SectionToggle({ open, onClick, icon, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center justify-between px-4 py-3.5 text-left text-sm font-semibold text-slate-800 transition-colors hover:bg-slate-50/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-400/30 dark:text-slate-200 dark:hover:bg-slate-800/50"
    >
      <span className="flex items-center gap-2.5">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
          <i className={`${icon} text-base`} aria-hidden />
        </span>
        {title}
      </span>
      <i className={`${open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-slate-400`} aria-hidden />
    </button>
  );
}

function SessionIdRow({ sessionId }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setCopied(false);
  }, [sessionId]);

  const handleCopy = async () => {
    const ok = await copyTextToClipboard(sessionId);
    if (!ok) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="border-b border-slate-100 px-4 py-3 dark:border-slate-700/60">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">
          Session ID
        </p>
        <button
          type="button"
          onClick={handleCopy}
          className="shrink-0 text-[11px] font-semibold text-teal-600 transition hover:text-teal-500"
        >
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <p
        className="mt-1 truncate font-mono text-xs font-semibold text-slate-800 dark:text-slate-200"
        title={sessionId}
      >
        {sessionId}
      </p>
    </div>
  );
}

function matchReasonLabel(code) {
  if (code === "device") return "Same device / browser";
  if (code === "email") return "Shared email";
  return code;
}

function WorkspaceBadge({ name, highlight = false }) {
  const label = (name && String(name).trim()) || "Workspace";
  return (
    <span
      className={`inline-flex max-w-full items-center truncate rounded-md px-1.5 py-0.5 text-[10px] font-semibold ring-1 ${
        highlight
          ? "bg-teal-50 text-teal-800 ring-teal-200/80 dark:bg-teal-950/50 dark:text-teal-200 dark:ring-teal-800/50"
          : "bg-violet-50 text-violet-800 ring-violet-200/80 dark:bg-violet-950/40 dark:text-violet-200 dark:ring-violet-800/50"
      }`}
      title={label}
    >
      <i className="ri-building-2-line mr-0.5 shrink-0 opacity-80" aria-hidden />
      {label}
    </span>
  );
}

function HistoricThreadCard({ thread, currentWorkspaceName, onViewTranscript }) {
  const wsName = thread.workspaceName ? String(thread.workspaceName).trim() : "";
  const isCurrentWs =
    currentWorkspaceName &&
    wsName &&
    wsName.localeCompare(String(currentWorkspaceName).trim(), undefined, { sensitivity: "base" }) === 0;

  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2.5 ring-1 ring-slate-100/80 dark:border-slate-700/60 dark:bg-slate-800/40 dark:ring-slate-700/40">
      <div className="mb-1.5 flex flex-wrap items-center gap-1">
        {wsName ? <WorkspaceBadge name={wsName} highlight={isCurrentWs} /> : null}
        {thread.matchReasons.map((code) => (
          <span
            key={code}
            className="rounded-md bg-white px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600 ring-1 ring-slate-200/80 dark:bg-slate-700 dark:text-slate-300 dark:ring-slate-600/60"
          >
            {matchReasonLabel(code)}
          </span>
        ))}
      </div>
      <p className="text-[11px] font-medium text-slate-400 dark:text-slate-500">{thread.startedAtLabel}</p>
      <p className="mt-0.5 line-clamp-2 text-xs leading-snug text-slate-800 dark:text-slate-200">{thread.preview}</p>
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">
          {thread.status} · {thread.messageCount} msgs
        </span>
        {thread.id ? (
          <button
            type="button"
            onClick={() => onViewTranscript(thread)}
            className="text-xs font-semibold text-teal-600 hover:text-teal-500"
          >
            View transcript
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default function UserDetailsPanel({
  conversationId,
  conversation,
  conversationLoading,
  createTicketFromLiveChat,
  onTicketCreated,
  workspaceId,
  centralInbox = false,
  sessionWorkspaceId = null,
  showTicketCreate = true,
  onVisitorEmailUpdated,
  teamInboxes = [],
  onAfterTeamInboxMove,
}) {
  const conv = conversation;
  const fromWorkspace =
    (conv?.workspaceName && String(conv.workspaceName).trim()) || "—";
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [creatingTicket, setCreatingTicket] = useState(false);
  const [zendeskEnabled, setZendeskEnabled] = useState(false);
  const zendeskDebug = useMemo(() => new ZendeskTicketCreateDebug(), []);
  const [historicModal, setHistoricModal] = useState(
    /** @type {{ sessionId: string, label: string } | null} */ (null)
  );
  const [userDataOpen, setUserDataOpen] = useState(true);
  const [historicOpen, setHistoricOpen] = useState(true);
  const [aiSummaryOpen, setAiSummaryOpen] = useState(true);
  const [emailDraft, setEmailDraft] = useState("");
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailError, setEmailError] = useState(/** @type {string | null} */ (null));
  const [emailSavedFlash, setEmailSavedFlash] = useState(false);
  const [teamFolderBusy, setTeamFolderBusy] = useState(false);
  const [teamFolderChoice, setTeamFolderChoice] = useState("");
  const { historicThreads, aiSummary, loading: historyLoading, error: historyError, refresh } = useCustomerHistory(
    conversationId,
    { workspaceId, centralInbox, sessionWorkspaceId },
  );

  const sessionId = String(conv?.id || conversationId || "").trim();
  const profileEmail = String(conv?.user?.email || conv?.identityEmails?.[0] || "").trim();
  const emailWorkspaceId =
    (sessionWorkspaceId && String(sessionWorkspaceId).trim()) ||
    (conv?.workspaceId && String(conv.workspaceId).trim()) ||
    (workspaceId && String(workspaceId).trim()) ||
    "";

  useEffect(() => {
    setEmailDraft(profileEmail);
    setEmailError(null);
    setEmailSavedFlash(false);
  }, [conversationId, profileEmail]);

  useEffect(() => {
    let cancelled = false;
    const wid =
      (sessionWorkspaceId && String(sessionWorkspaceId).trim()) ||
      (workspaceId && String(workspaceId).trim()) ||
      "";
    if (!wid || !showTicketCreate) {
      setZendeskEnabled(false);
      return undefined;
    }
    void zendeskDebug.isEnabled(wid).then((enabled) => {
      if (!cancelled) setZendeskEnabled(Boolean(enabled));
    });
    return () => {
      cancelled = true;
    };
  }, [sessionWorkspaceId, workspaceId, showTicketCreate, zendeskDebug]);

  const folderWorkspaceId =
    (sessionWorkspaceId && String(sessionWorkspaceId).trim()) ||
    (conv?.workspaceId && String(conv.workspaceId).trim()) ||
    (workspaceId && String(workspaceId).trim()) ||
    "";

  const resolvedTeamInboxId = useMemo(() => {
    const t = conv?.team;
    if (!t || t === "Support" || !Array.isArray(teamInboxes) || !teamInboxes.length) return "";
    const m = teamInboxes.find((x) => x.label === t);
    return m?.id ? String(m.id) : "";
  }, [conv?.team, teamInboxes]);

  useEffect(() => {
    setTeamFolderChoice(resolvedTeamInboxId);
  }, [conversationId, resolvedTeamInboxId]);

  const canEditTeamFolder =
    Boolean(folderWorkspaceId) &&
    typeof onAfterTeamInboxMove === "function" &&
    Array.isArray(teamInboxes);

  const handleApplyTeamFolder = async () => {
    if (!folderWorkspaceId || !conversationId || teamFolderBusy) return;
    setTeamFolderBusy(true);
    try {
      const tid = teamFolderChoice.trim() || null;
      await patchSessionTeamInbox({
        sessionId: conversationId,
        teamInboxId: tid,
        workspaceId: folderWorkspaceId,
      });
      onAfterTeamInboxMove?.();
    } catch (err) {
      surfaceInboxHttpError(err);
    } finally {
      setTeamFolderBusy(false);
    }
  };

  const emailDirty =
    emailDraft.trim().toLowerCase() !== profileEmail.trim().toLowerCase();
  const emailValid = EMAIL_RE.test(emailDraft.trim());

  const handleSaveEmail = async () => {
    if (!conv?.id || !emailDirty || emailSaving) return;
    if (!emailValid) {
      setEmailError("Enter a valid email address.");
      return;
    }
    if (!emailWorkspaceId) {
      setEmailError("Workspace is required to update this email.");
      return;
    }
    if (!isHumanAgentInboxLive()) {
      setEmailError("Live inbox is required to update visitor email.");
      return;
    }
    setEmailSaving(true);
    setEmailError(null);
    try {
      const result = await patchVisitorEmail({
        sessionId: String(conv.id),
        email: emailDraft.trim(),
        workspaceId: emailWorkspaceId,
      });
      setEmailDraft(result.email);
      setEmailSavedFlash(true);
      onVisitorEmailUpdated?.(result);
      refresh();
      window.setTimeout(() => setEmailSavedFlash(false), 2000);
    } catch (err) {
      surfaceInboxHttpError(err, { fallback: "Could not update visitor email." });
      setEmailError(err instanceof Error ? err.message : "Could not update email.");
    } finally {
      setEmailSaving(false);
    }
  };
  const historicByWorkspace = useMemo(
    () => groupHistoricThreadsByWorkspace(historicThreads),
    [historicThreads],
  );

  const sessionWorkspaceLabel = fromWorkspace !== "—" ? fromWorkspace : null;

  const workspaceBySessionId = useMemo(() => {
    const map = new Map();
    for (const t of historicThreads) {
      if (t.id) {
        map.set(String(t.id), {
          workspaceName: t.workspaceName ? String(t.workspaceName) : null,
          workspaceId: t.workspaceId ? String(t.workspaceId) : null,
        });
      }
    }
    if (conv?.id && sessionWorkspaceLabel) {
      map.set(String(conv.id), {
        workspaceName: sessionWorkspaceLabel,
        workspaceId: conv.workspaceId ? String(conv.workspaceId) : null,
      });
    }
    return map;
  }, [historicThreads, conv?.id, conv?.workspaceId, sessionWorkspaceLabel]);

  const handleViewHistoricTranscript = (thread) => {
    setHistoricModal({
      sessionId: String(thread.id),
      workspaceId: thread.workspaceId ? String(thread.workspaceId) : null,
      label: String(thread.startedAtLabel || thread.preview || "").slice(0, 80),
    });
  };

  useEffect(() => {
    setTicketModalOpen(false);
    setHistoricModal(null);
  }, [conversationId]);

  if (conversationLoading && !conv) {
    return (
      <div className="flex h-full min-h-0 w-full min-w-0 flex-col items-center justify-center gap-3 overflow-hidden bg-white/95 px-4 text-center backdrop-blur-md">
        <div className="h-8 w-8 animate-pulse rounded-full bg-slate-200" />
        <p className="text-xs font-medium text-slate-500">Loading profile…</p>
      </div>
    );
  }

  if (!conv) {
    return (
      <div className="flex h-full min-h-0 w-full min-w-0 flex-col items-center justify-center overflow-hidden bg-white/95 px-4 text-center backdrop-blur-md">
        <p className="text-sm font-medium text-slate-500">Select a conversation to see visitor details.</p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden bg-white/95 backdrop-blur-md">
      <div className="scrollbar-thin flex-1 overflow-y-auto">
        <div>
            {sessionId ? <SessionIdRow sessionId={sessionId} /> : null}

            <div className="border-b border-slate-100 px-4 py-4">
              <div className="mb-3 flex items-center justify-between text-xs">
                <span className="font-medium text-slate-500">Assignee</span>
                <span className="font-semibold text-slate-800">
                  {conv.assignee === "Unassigned"
                    ? conv.suggestedAssignee || "Unassigned"
                    : conv.assignee}
                </span>
              </div>
            </div>

            {canEditTeamFolder ? (
              <div className="border-b border-slate-100 px-4 py-4">
                <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">Folder</p>
                <label htmlFor="team-inbox-move" className="sr-only">
                  Team inbox folder
                </label>
                <select
                  id="team-inbox-move"
                  className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-2.5 pr-8 text-xs font-medium text-slate-800 shadow-sm"
                  value={teamFolderChoice}
                  onChange={(e) => setTeamFolderChoice(e.target.value)}
                  disabled={teamFolderBusy}
                >
                  <option value="">Default (no folder)</option>
                  {teamInboxes.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="mt-2 w-full rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 text-xs font-semibold text-teal-900 hover:bg-teal-100 disabled:opacity-50"
                  disabled={teamFolderBusy || teamFolderChoice === resolvedTeamInboxId}
                  onClick={() => void handleApplyTeamFolder()}
                >
                  {teamFolderBusy ? "Saving…" : "Save folder"}
                </button>
              </div>
            ) : (
              <div className="border-b border-slate-100 px-4 py-4">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium text-slate-500">Team inbox</span>
                  <span className="flex items-center gap-2 font-semibold text-slate-800">
                    <span className="h-2 w-2 rounded-full bg-amber-400 shadow-sm shadow-amber-400/50" />
                    {conv.team}
                  </span>
                </div>
              </div>
            )}

            {showTicketCreate && (
              <div className="border-b border-slate-100 px-4 py-4">
                <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">Ticket</p>
                <button
                  type="button"
                  onClick={() => setTicketModalOpen(true)}
                  disabled={creatingTicket}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-teal-200/80 bg-teal-50/50 px-3 py-2.5 text-sm font-semibold text-teal-800 ring-1 ring-teal-100/60 transition hover:bg-teal-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-400/50 disabled:opacity-50"
                >
                  <i className="ri-ticket-2-line text-lg" aria-hidden />
                  {creatingTicket ? "Creating ticket…" : "Create ticket"}
                </button>
                <p className="mt-2 text-[11px] leading-relaxed text-slate-500">
                  Attaches this conversation, assigns the next ticket number (from 1001), and emails the confirmation to the
                  address you specify.
                </p>
              </div>
            )}

            <div className="border-b border-slate-100">
              <SectionToggle open={userDataOpen} onClick={() => setUserDataOpen(!userDataOpen)} icon="ri-user-line" title="Profile" />
              {userDataOpen && (
                <div className="space-y-2.5 px-4 pb-4">
                  {[
                    { label: "Name", value: conv.user.name },
                    { label: "Company", value: conv.user.company },
                    { label: "Type", value: conv.user.type },
                    { label: "Location", value: conv.user.location },
                  ].map((row) => (
                    <div key={row.label} className="flex items-start justify-between gap-3 text-xs">
                      <span className="shrink-0 font-medium text-slate-400">{row.label}</span>
                      <span className="text-right font-semibold text-slate-800 dark:text-slate-200">{row.value}</span>
                    </div>
                  ))}
                  <div className="flex items-start justify-between gap-3 text-xs">
                    <span className="shrink-0 pt-1.5 font-medium text-slate-400">Email</span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <input
                          type="email"
                          value={emailDraft}
                          onChange={(e) => {
                            setEmailDraft(e.target.value);
                            setEmailError(null);
                            setEmailSavedFlash(false);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              void handleSaveEmail();
                            }
                          }}
                          disabled={emailSaving}
                          aria-label="Visitor email"
                          className="w-full min-w-0 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-right text-xs font-semibold text-slate-800 shadow-sm outline-none ring-teal-400/40 focus:border-teal-300 focus:ring-2 disabled:opacity-60 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                          placeholder="visitor@example.com"
                        />
                        <button
                          type="button"
                          onClick={() => void handleSaveEmail()}
                          disabled={!emailDirty || emailSaving || !emailValid}
                          className="shrink-0 rounded-lg bg-teal-600 px-2 py-1.5 text-[11px] font-semibold text-white shadow-sm transition hover:bg-teal-500 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          {emailSaving ? "…" : "Save"}
                        </button>
                      </div>
                      {emailError ? (
                        <p className="mt-1 text-right text-[10px] font-medium text-rose-600 dark:text-rose-400">{emailError}</p>
                      ) : emailSavedFlash ? (
                        <p className="mt-1 text-right text-[10px] font-medium text-teal-700 dark:text-teal-300">Saved</p>
                      ) : emailDirty && !emailValid ? (
                        <p className="mt-1 text-right text-[10px] font-medium text-amber-700 dark:text-amber-300">Invalid email</p>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex items-start justify-between gap-3 text-xs">
                    <span className="shrink-0 font-medium text-slate-400">From Workspace</span>
                    {sessionWorkspaceLabel ? (
                      <WorkspaceBadge name={sessionWorkspaceLabel} highlight />
                    ) : (
                      <span className="text-right font-semibold text-slate-800 dark:text-slate-200">{fromWorkspace}</span>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="border-b border-slate-100 dark:border-slate-700/60">
              <SectionToggle
                open={historicOpen}
                onClick={() => setHistoricOpen(!historicOpen)}
                icon="ri-chat-history-line"
                title="Historic conversations"
              />
              {historicOpen && (
                <div className="space-y-2 px-4 pb-4">
                  <p className="text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
                    {centralInbox
                      ? "Prior chats linked by this visitor’s email, grouped by workspace across every workspace you can access."
                      : "Prior chats from the same device session or any email linked to this visitor (including addresses shared in earlier conversations)."}
                  </p>
                  {historyLoading && <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Loading history…</p>}
                  {historyError && (
                    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-rose-200 bg-rose-50/80 px-2 py-1.5 text-xs text-rose-800 dark:border-rose-800/50 dark:bg-rose-950/40 dark:text-rose-300">
                      <span>{historyError.message}</span>
                      <button type="button" onClick={refresh} className="font-semibold underline">
                        Retry
                      </button>
                    </div>
                  )}
                  {!historyLoading && !historyError && historicThreads.length === 0 && (
                    <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500 ring-1 ring-slate-100 dark:bg-slate-800/50 dark:text-slate-400 dark:ring-slate-700/50">
                      No linked historic threads for this visitor yet.
                    </p>
                  )}
                  {!historyLoading &&
                    historicByWorkspace.map((group) => (
                      <div key={group.workspaceId || group.workspaceName} className="space-y-2">
                        {historicByWorkspace.length > 1 || centralInbox ? (
                          <div className="flex items-center justify-between gap-2 pt-1">
                            <WorkspaceBadge
                              name={group.workspaceName}
                              highlight={
                                sessionWorkspaceLabel
                                  ? group.workspaceName.localeCompare(sessionWorkspaceLabel, undefined, {
                                      sensitivity: "base",
                                    }) === 0
                                  : false
                              }
                            />
                            <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">
                              {group.threads.length} thread{group.threads.length === 1 ? "" : "s"}
                            </span>
                          </div>
                        ) : null}
                        {group.threads.map((thread) => (
                          <HistoricThreadCard
                            key={thread.id}
                            thread={thread}
                            currentWorkspaceName={sessionWorkspaceLabel}
                            onViewTranscript={handleViewHistoricTranscript}
                          />
                        ))}
                      </div>
                    ))}
                </div>
              )}
            </div>

            <div className="border-b border-slate-100">
              <SectionToggle
                open={aiSummaryOpen}
                onClick={() => setAiSummaryOpen(!aiSummaryOpen)}
                icon="ri-sparkling-2-line"
                title="Customer summary (AI)"
              />
              {aiSummaryOpen && (
                <div className="space-y-2 px-4 pb-4">
                  {historyLoading && <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Generating summary…</p>}
                  {!historyLoading && aiSummary && (
                    <>
                      {aiSummary.error && (
                        <p className="rounded-lg border border-amber-200 bg-amber-50/90 px-3 py-2 text-xs leading-relaxed text-amber-900 dark:border-amber-800/50 dark:bg-amber-950/40 dark:text-amber-200">
                          {aiSummary.error}
                        </p>
                      )}
                      {aiSummary.summary ? (
                        <p className="rounded-lg border border-teal-200 bg-teal-50 px-3 py-2.5 text-xs leading-relaxed text-slate-800 dark:border-teal-800/50 dark:bg-teal-950/40 dark:text-slate-200">
                          {aiSummary.summary}
                        </p>
                      ) : null}
                      {Array.isArray(aiSummary.evidence) && aiSummary.evidence.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">Evidence (user)</p>
                          {aiSummary.evidence.map((ev, i) => {
                            const wsMeta = ev.sessionId ? workspaceBySessionId.get(String(ev.sessionId)) : null;
                            return (
                            <div
                              key={`${ev.sessionId}-${i}`}
                              className="rounded-lg border border-slate-200/90 bg-slate-50/80 px-2.5 py-2 text-[11px] leading-relaxed text-slate-800 ring-1 ring-slate-100/80 dark:border-slate-700/60 dark:bg-slate-800/50 dark:text-slate-200 dark:ring-slate-700/40"
                            >
                              {wsMeta?.workspaceName ? (
                                <div className="mb-1">
                                  <WorkspaceBadge name={wsMeta.workspaceName} />
                                </div>
                              ) : null}
                              {ev.sessionId && (
                                <p className="mb-0.5 font-mono text-[10px] text-slate-500 dark:text-slate-400">Session {ev.sessionId}</p>
                              )}
                              {ev.sentAt && (
                                <p className="mb-0.5 text-[10px] text-slate-400 dark:text-slate-500">{new Date(ev.sentAt).toLocaleString()}</p>
                              )}
                              <p className="whitespace-pre-wrap text-slate-800 dark:text-slate-200">“{ev.text}”</p>
                            </div>
                            );
                          })}
                        </div>
                      )}
                      {aiSummary.highlights?.length > 0 && (
                        <ul className="list-inside list-disc space-y-1 text-[11px] leading-relaxed text-slate-600 dark:text-slate-300">
                          {aiSummary.highlights.map((line, i) => (
                            <li key={i}>{line}</li>
                          ))}
                        </ul>
                      )}
                      {aiSummary.disclaimer ? (
                        <p className="text-[10px] leading-relaxed text-slate-400 dark:text-slate-500">{aiSummary.disclaimer}</p>
                      ) : null}
                      {aiSummary.generatedAt && (
                        <p className="text-[10px] font-mono text-slate-400 dark:text-slate-500">
                          Source: {aiSummary.source} · {new Date(aiSummary.generatedAt).toLocaleString()}
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
        </div>
      </div>

      {showTicketCreate && (
        <CreateTicketModal
          open={ticketModalOpen}
          onClose={() => setTicketModalOpen(false)}
          conversation={conv}
          zendeskEnabled={zendeskEnabled}
          onSubmittingChange={setCreatingTicket}
          onSubmit={async (payload) => {
            if (!conv || !createTicketFromLiveChat || !onTicketCreated) return;
            const { ticket, warnings } = await createTicketFromLiveChat({
              conversationId: conv.id,
              subject: payload.subject,
              requesterEmail: payload.requesterEmail,
            });
            onTicketCreated(ticket, warnings);
          }}
        />
      )}

      <HistoricThreadModal
        open={Boolean(historicModal?.sessionId)}
        sessionId={historicModal?.sessionId ?? ""}
        workspaceId={historicModal?.workspaceId ?? workspaceId}
        centralInbox={centralInbox}
        sessionWorkspaceId={historicModal?.workspaceId ?? sessionWorkspaceId}
        previewLabel={historicModal?.label ?? ""}
        onClose={() => setHistoricModal(null)}
      />
    </div>
  );
}
